import React, { useCallback, useEffect, useRef, useState } from 'react';
import { withRouter } from 'react-router-dom';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import {
    Badge, Button, Card, CardBody, Col, Collapse, Container,
    Input, InputGroup, Label, Row, Spinner,
} from 'reactstrap';
import Select from 'react-select';
import Flatpickr from 'react-flatpickr';
import { mergeOperationalDateCaps } from '../../../utils/flatpickrOperational';
import moment from 'moment';
import 'flatpickr/dist/themes/material_blue.css';

import { pageState, themeTypeState } from '../../../state/GlobalState';
import { permissionState } from '../../../api/state/AuthState';
import { BarcodeScannerModal } from '../../../custom/components/BarcodeScannerModal';
import { ConfirmDialog } from '../../../custom/components/ConfirmDialog';
import { DynamicModal } from '../../../custom/components/DynamicModal';
import notify from '../../../custom/helpers/Notify';
import { BagViewModal } from './BagViewModal';
import { LocateModal, ReceiveModal, DeliverModal, ForwardModal, DisposeModal } from './BagActionModals';

import {
    searchBagsRequest,
    updateDataRequest,
    deleteDataRequest,
    getBagStatusListRequest,
    getForwardTypeListRequest,
    getDamageBagClaimListRequest,
} from '../../../api/controller/BagFinderController';
import { getSignatureDataRequest } from '../../../api/controller/BagController';

import {
    dataStructureState,
    bagBagStatusState,
    bagForwardTypeState,
    bagArrivalFlightState,
    bagDepartureFlightState,
    bagDamageBagClaimState,
    modalConfigDefault,
} from './state';

// ─── Constants ───────────────────────────────────────────────────────────────

const STATUS_COLOR = {
    TRACING:    'secondary',
    LOCATED:    'info',
    PROCESSING: 'dark',
    RECEIVED:   'warning',
    DELIVERED:  'success',
    FORWARDED:  'primary',
};

const STATUS_BORDER = {
    TRACING:    '#6c757d',
    LOCATED:    '#0dcaf0',
    PROCESSING: '#495057',
    RECEIVED:   '#ffc107',
    DELIVERED:  '#198754',
    FORWARDED:  '#0d6efd',
};

const TYPE_COLOR = {
    AHL:       'primary',
    DPR:       'danger',
    OHD:       'warning',
    UNCLAIMED: 'secondary',
    CLAIMS:    'dark',
};

const TYPE_OPTIONS = [
    { label: 'AHL',       value: 'AHL' },
    { label: 'DPR',       value: 'DPR' },
    { label: 'OHD',       value: 'OHD' },
    { label: 'UNCLAIMED', value: 'UNCLAIMED' },
    { label: 'CLAIMS',    value: 'CLAIMS' },
];

const DEFAULT_CASE_FLIGHT_DATE = [
    moment().subtract(1, 'year').format('YYYY-MM-DD'),
    moment().format('YYYY-MM-DD'),
];

const DEFAULT_FILTERS = {
    Type:            null,
    Reference:       '',
    FileReference:   '',
    PassengerName:   '',
    CaseFlight:      '',
    CaseFlightDate:  DEFAULT_CASE_FLIGHT_DATE,
    TagNumber:       '',
    ColorType:       '',
    Status:          null,
    LocationHistory: '',
    RushTagNumber:   '',
    ReceiveDate:     [],
    DeliverDate:     [],
    ForwardDate:     [],
    ReceivedFlight:  '',
    ForwardFlight:   '',
    ForwardType:     null,
};

/** Default filter shape with case flight date range recomputed from “today” (for reset / reference drill-down). */
const freshDefaultFilters = () => ({
    ...DEFAULT_FILTERS,
    CaseFlightDate: [
        moment().subtract(1, 'year').format('YYYY-MM-DD'),
        moment().format('YYYY-MM-DD'),
    ],
});

// ─── Component ───────────────────────────────────────────────────────────────

const BagFinder = () => {
    const setPage          = useSetRecoilState(pageState);
    const themeType        = useRecoilValue(themeTypeState);
    const dataStructure    = useRecoilValue(dataStructureState);
    const permissions      = useRecoilValue(permissionState);

    const canLocate = Boolean(permissions?.includes('locate-caseFiles'));
    const canReceive = Boolean(permissions?.includes('receive-caseFiles'));
    const canDeliver = Boolean(permissions?.includes('deliver-caseFiles'));
    const canForward = Boolean(permissions?.includes('forward-caseFiles'));
    const canDispose = Boolean(permissions?.includes('dispose-caseFiles'));

    const setBagStatusList    = useSetRecoilState(bagBagStatusState);
    const setForwardTypeList  = useSetRecoilState(bagForwardTypeState);
    const setDamageBagClaimList = useSetRecoilState(bagDamageBagClaimState);
    const setArrivalFlightList  = useSetRecoilState(bagArrivalFlightState);
    const setDepartureFlightList = useSetRecoilState(bagDepartureFlightState);

    // Local select lists for filter dropdowns (don't need Recoil)
    const [statusOptions,      setStatusOptions]      = useState([]);
    const [forwardTypeOptions, setForwardTypeOptions] = useState([]);

    const [searchQuery,  setSearchQuery]  = useState('');
    const [filters,      setFilters]      = useState(DEFAULT_FILTERS);
    const [filtersOpen,  setFiltersOpen]  = useState(false);
    const [results,      setResults]      = useState([]);
    const [loading,      setLoading]      = useState(false);
    const [searched,     setSearched]     = useState(false);
    const [ovf,          setOvf]          = useState(false);
    const [scannerOpen,    setScannerOpen]    = useState(false);
    const [viewOpen,       setViewOpen]       = useState(false);
    const [viewBag,        setViewBag]        = useState(null);
    const [actionModal,    setActionModal]    = useState(null); // { type: 'locate'|'receive'|'deliver'|'forward'|'dispose', bag }

    const formModes = {
        update: [{ name: 'Default Update', action: async (data) => {
            const response = await updateDataRequest(data);
            if (response?.Status === 'success' && response.Data) {
                setResults(prev => prev.map(r => r.id === response.Data.id ? response.Data : r));
            }
            return response;
        }}],
        view: [],
    };

    const [cuModalConfig, setCuModalConfig] = useState({
        visible: false, header: 'Bag', type: null, typeKey: 0,
        data: {}, ...modalConfigDefault,
    });
    const [confirmVisible, setConfirmVisible] = useState(false);
    const [confirmID,      setConfirmID]      = useState(null);
    const [confirmParams,  setConfirmParams]  = useState({});
    const [confirmConfig,  setConfirmConfig]  = useState({ fn: null, title: null, body: null });

    const searchRef      = useRef(null);
    const doSearchRef    = useRef(null);
    const prevConfirmVis = useRef(false);

    // ── Page init ──────────────────────────────────────────────────────────

    useEffect(() => {
        setPage('bagFinder');
    }, [setPage]);

    // ── Load select data for filter panel + update modal ───────────────────

    useEffect(() => {
        const load = async () => {
            const [statusRes, ftRes, claimRes] = await Promise.all([
                getBagStatusListRequest(),
                getForwardTypeListRequest(),
                getDamageBagClaimListRequest(),
            ]);

            if (statusRes?.Status !== 'error') {
                const mapped = statusRes.data?.map(v => ({ label: v.name, value: v.name })) ?? [];
                setStatusOptions(mapped);
                // Also populate Recoil atom so the update modal form works
                setBagStatusList({
                    ovf: statusRes.ovf,
                    data: statusRes.data?.map(v => ({ label: v.name, value: v.id })) ?? [],
                });
            }
            if (ftRes?.Status !== 'error') {
                const mapped = ftRes.data?.map(v => ({ label: v.name, value: v.id })) ?? [];
                setForwardTypeOptions(mapped);
                setForwardTypeList({ ovf: ftRes.ovf, data: mapped });
            }
            if (claimRes?.Status !== 'error') {
                setDamageBagClaimList({
                    ovf: claimRes.ovf,
                    data: claimRes.data?.map(v => ({ label: v.name, value: v.id })) ?? [],
                });
            }
        };
        load();

        return () => {
            // Clear atoms on unmount so the bags page reloads fresh
            setBagStatusList({ data: [], ovf: false });
            setForwardTypeList({ data: [], ovf: false });
            setDamageBagClaimList({ data: [], ovf: false });
            setArrivalFlightList({ data: [], ovf: false });
            setDepartureFlightList({ data: [], ovf: false });
        };
        // eslint-disable-next-line
    }, []);

    // ── Keyboard shortcut: / to focus search ───────────────────────────────

    useEffect(() => {
        const handler = (e) => {
            if (e.key === '/' && !['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName)) {
                e.preventDefault();
                searchRef.current?.focus();
            }
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, []);


    // ── Auto-refresh after delete confirm closes ───────────────────────────

    useEffect(() => {
        if (prevConfirmVis.current && !confirmVisible && searched) {
            doSearchRef.current?.();
        }
        prevConfirmVis.current = confirmVisible;
        // eslint-disable-next-line
    }, [confirmVisible]);

    // ── Helpers ────────────────────────────────────────────────────────────

    const activeFilterCount = Object.entries(filters).filter(([, v]) =>
        Array.isArray(v) ? v.length > 0 : (v !== null && v !== '')
    ).length;

    // Status-driven date visibility
    const statusVal       = filters.Status?.value;
    const showReceiveDate = !statusVal || ['RECEIVED', 'DELIVERED', 'FORWARDED'].includes(statusVal);
    const showDeliverDate = !statusVal || statusVal === 'DELIVERED';
    const showForwardDate = !statusVal || statusVal === 'FORWARDED';

    const addDateRange = (d, key, arr) => {
        if (!arr || arr.length === 0) return;
        d[`${key}From`] = arr[0];
        d[`${key}To`]   = arr.length > 1 ? arr[1] : arr[0];
    };

    const buildPostData = useCallback((queryOverride, filterSnapshot) => {
        const f = filterSnapshot !== undefined ? filterSnapshot : filters;
        const q = queryOverride !== undefined ? queryOverride : searchQuery;
        const sv = f.Status?.value;
        const canReceive = !sv || ['RECEIVED', 'DELIVERED', 'FORWARDED'].includes(sv);
        const canDeliver = !sv || sv === 'DELIVERED';
        const canForward = !sv || sv === 'FORWARDED';
        const d = {};
        if (q.trim())                       d.SearchQuery     = q.trim();
        if (f.Type)                         d.Type            = f.Type.value;
        if (f.Reference.trim())           d.Reference       = f.Reference.trim();
        if (f.FileReference.trim())       d.FileReference   = f.FileReference.trim();
        if (f.PassengerName.trim())       d.PassengerName   = f.PassengerName.trim();
        if (f.CaseFlight.trim())          d.CaseFlight      = f.CaseFlight.trim();
        addDateRange(d, 'CaseFlightDate', f.CaseFlightDate);
        if (f.TagNumber.trim())           d.TagNumber       = f.TagNumber.trim();
        if (f.ColorType.trim())           d.ColorType       = f.ColorType.trim();
        if (f.Status)                     d.Status          = f.Status.value;
        if (f.LocationHistory.trim())     d.LocationHistory = f.LocationHistory.trim();
        if (f.RushTagNumber.trim())       d.RushTagNumber   = f.RushTagNumber.trim();
        if (canReceive)  addDateRange(d, 'ReceiveDate', f.ReceiveDate);
        if (canDeliver)  addDateRange(d, 'DeliverDate', f.DeliverDate);
        if (canForward)  addDateRange(d, 'ForwardDate', f.ForwardDate);
        if (f.ReceivedFlight.trim())     d.ReceivedFlight  = f.ReceivedFlight.trim();
        if (f.ForwardFlight.trim())       d.ForwardFlight   = f.ForwardFlight.trim();
        if (f.ForwardType)                d.ForwardType     = f.ForwardType.value;
        return d;
    }, [searchQuery, filters]);

    const doSearch = useCallback(async (queryOverride, filterSnapshot) => {
        setFiltersOpen(false);
        const postData = buildPostData(queryOverride, filterSnapshot);
        if (Object.keys(postData).length === 0) {
            notify({ status: 'warning', message: 'Enter a search term or apply a filter first.' });
            return;
        }
        setLoading(true);
        setSearched(true);
        const response = await searchBagsRequest(postData);
        if (response?.Status !== 'error') {
            setResults(response.data ?? []);
            setOvf(response.ovf ?? false);
        } else {
            notify({ status: 'error', message: response.Message });
        }
        setLoading(false);
    }, [buildPostData]);

    // Keep ref current so useEffects above can call it without stale closure
    doSearchRef.current = doSearch;

    const applyScannedSearch = useCallback((text) => {
        const trimmed = (text || '').trim();
        if (!trimmed) return;
        setSearchQuery(trimmed);
        doSearch(trimmed);
    }, [doSearch]);

    const setFilter = (key, value) => setFilters(prev => ({ ...prev, [key]: value }));

    /** Default filters only (fresh flight-date window), clear search text, result cards, and return to the pre-search prompt. */
    const resetFinderState = useCallback(() => {
        setFilters(freshDefaultFilters());
        setSearchQuery('');
        setResults([]);
        setSearched(false);
        setOvf(false);
        setFiltersOpen(false);
    }, []);

    /** Reset all filters to defaults (fresh case flight date window), set reference, clear main search, and run query. */
    const searchByCaseReference = useCallback((reference) => {
        const ref = (reference || '').trim();
        if (!ref) return;
        const nextFilters = { ...freshDefaultFilters(), Reference: ref };
        setSearchQuery('');
        setFilters(nextFilters);
        void doSearch('', nextFilters);
    }, [doSearch]);

    const handleFilterEnter = (e) => { if (e.key === 'Enter') doSearch(); };

    // ── Modal helpers ──────────────────────────────────────────────────────

    const withSignature = async (bag) => {
        if (!bag?.delivery_signature) return bag;
        const res = await getSignatureDataRequest({ id: bag.id });
        if (res?.Status === 'success') return { ...bag, delivery_signature_data: res.Data };
        return bag;
    };

    const toggleCUModal = useCallback(({ row = {}, mode = null, modeKey = 0, size = null } = {}) => {
        setCuModalConfig(prev => ({
            ...prev,
            visible:  !prev.visible,
            type:     typeof mode === 'string' ? mode : null,
            typeKey:  modeKey,
            data:     row,
            size:     size || prev.size,
        }));
        // eslint-disable-next-line
    }, [cuModalConfig.type]);

    const toggleConfirmModal = useCallback(({ row = {}, params = {}, request = null, title = null, body = null } = {}) => {
        setConfirmID(row?.id ?? null);
        setConfirmParams(params);
        setConfirmConfig({ fn: request, title, body });
        setConfirmVisible(v => !v);
    }, []);

    // ── Render helpers ─────────────────────────────────────────────────────

    const statusBadge = (bag) => {
        const name = bag.bag_status?.name;
        return <Badge color={STATUS_COLOR[name] || 'secondary'} className="me-1">{name || '—'}</Badge>;
    };

    const typeBadge = (type) => (
        <Badge color={TYPE_COLOR[type] || 'secondary'} pill className="text-uppercase">{type}</Badge>
    );

    const selectStyles = themeType === 'dark' ? {
        control: (b) => ({ ...b, backgroundColor: '#2a3042', borderColor: '#32394e', color: '#a6b0cf' }),
        menu:    (b) => ({ ...b, backgroundColor: '#2a3042' }),
        option:  (b, s) => ({ ...b, backgroundColor: s.isFocused ? '#32394e' : '#2a3042', color: '#a6b0cf' }),
        singleValue: (b) => ({ ...b, color: '#a6b0cf' }),
        input:   (b) => ({ ...b, color: '#a6b0cf' }),
    } : {};

    // ── Render ─────────────────────────────────────────────────────────────

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>

                    {/* ── Header ─────────────────────────────────────────── */}
                    <Row className="mb-3 align-items-center">
                        <Col>
                            <h4 className="mb-0 d-flex align-items-center gap-2" style={{ color: themeType === 'dark' ? '#fff' : undefined }}>
                                <i className="mdi mdi-magnify text-primary" />
                                Bag Finder
                            </h4>
                        </Col>
                        <Col xs="auto">
                            <small style={{ color: themeType === 'dark' ? '#adb5bd' : undefined }}>
                                Press <kbd>/</kbd> to focus search
                            </small>
                        </Col>
                    </Row>

                    {/* ── Search bar ─────────────────────────────────────── */}
                    <Row className="mb-2">
                        <Col xs={12}>
                            <InputGroup size="lg">
                                <Input
                                    innerRef={searchRef}
                                    value={searchQuery}
                                    onChange={e => setSearchQuery(e.target.value)}
                                    onKeyDown={e => { if (e.key === 'Enter') doSearch(); }}
                                    placeholder="Search by tag number, reference, passenger name…"
                                    style={{ fontSize: '1rem' }}
                                />
                                <Button
                                    color="secondary"
                                    outline
                                    title="Scan barcode or QR with camera"
                                    onClick={() => setScannerOpen(true)}
                                    style={{ padding: '0 14px', ...(themeType === 'dark' ? { color: '#adb5bd', borderColor: '#6c757d' } : {}) }}
                                >
                                    <i className="mdi mdi-barcode-scan font-size-20" />
                                </Button>
                                <Button
                                    color={filtersOpen ? 'info' : 'secondary'}
                                    outline
                                    onClick={() => setFiltersOpen(o => !o)}
                                    style={{ padding: '0 14px', ...(!filtersOpen && themeType === 'dark' ? { color: '#adb5bd', borderColor: '#6c757d' } : {}) }}
                                >
                                    <i className="mdi mdi-filter-variant me-1" />
                                    Filters
                                    {activeFilterCount > 0 && (
                                        <Badge color="danger" pill className="ms-1" style={{ fontSize: '0.65rem', verticalAlign: 'middle' }}>
                                            {activeFilterCount}
                                        </Badge>
                                    )}
                                </Button>
                                <Button
                                    color="secondary"
                                    outline
                                    title="Reset filters to defaults and clear results"
                                    onClick={resetFinderState}
                                    disabled={loading}
                                    style={{ padding: '0 14px', ...(themeType === 'dark' ? { color: '#adb5bd', borderColor: '#6c757d' } : {}) }}
                                >
                                    <i className="mdi mdi-restore me-1" />
                                    <span className="d-none d-md-inline">Reset</span>
                                </Button>
                                <Button
                                    color="primary"
                                    onClick={() => doSearch()}
                                    disabled={loading}
                                    style={{ minWidth: '100px' }}
                                >
                                    {loading
                                        ? <Spinner size="sm" />
                                        : <><i className="mdi mdi-magnify me-1" />Search</>
                                    }
                                </Button>
                            </InputGroup>
                        </Col>
                    </Row>

                    {/* ── Filter panel ───────────────────────────────────── */}
                    <Collapse isOpen={filtersOpen}>
                        <Card className="mb-3" style={{ border: '1px solid #556ee6' }}>
                            <CardBody className="pb-1">

                                {/* Case File */}
                                <p className="text-muted fw-semibold text-uppercase mb-2" style={{ fontSize: '0.7rem', letterSpacing: '0.08em' }}>
                                    Case File
                                </p>
                                <Row className="g-2 mb-2">
                                    <Col xs={12} md={3}>
                                        <Label className="form-label small mb-1">Type</Label>
                                        <Select
                                            isClearable
                                            options={TYPE_OPTIONS}
                                            value={filters.Type}
                                            onChange={v => setFilter('Type', v)}
                                            placeholder="Any type…"
                                            styles={selectStyles}
                                        />
                                    </Col>
                                    <Col xs={12} md={3}>
                                        <Label className="form-label small mb-1">Reference</Label>
                                        <Input value={filters.Reference} onChange={e => setFilter('Reference', e.target.value)} onKeyDown={handleFilterEnter} placeholder="e.g. AHL-2024-001" />
                                    </Col>
                                    <Col xs={12} md={3}>
                                        <Label className="form-label small mb-1">File Reference</Label>
                                        <Input value={filters.FileReference} onChange={e => setFilter('FileReference', e.target.value)} onKeyDown={handleFilterEnter} placeholder="World Tracer ref…" />
                                    </Col>
                                    <Col xs={12} md={3}>
                                        <Label className="form-label small mb-1">Passenger</Label>
                                        <Input value={filters.PassengerName} onChange={e => setFilter('PassengerName', e.target.value)} onKeyDown={handleFilterEnter} placeholder="First or last name…" />
                                    </Col>
                                    <Col xs={12} md={3}>
                                        <Label className="form-label small mb-1">Flight</Label>
                                        <Input value={filters.CaseFlight} onChange={e => setFilter('CaseFlight', e.target.value)} onKeyDown={handleFilterEnter} placeholder="e.g. MQ123…" />
                                    </Col>
                                </Row>

                                {/* Bag */}
                                <p className="text-muted fw-semibold text-uppercase mb-2 mt-3" style={{ fontSize: '0.7rem', letterSpacing: '0.08em' }}>
                                    Bag
                                </p>
                                <Row className="g-2 mb-2">
                                    <Col xs={12} md={3}>
                                        <Label className="form-label small mb-1">Tag Number</Label>
                                        <Input value={filters.TagNumber} onChange={e => setFilter('TagNumber', e.target.value)} onKeyDown={handleFilterEnter} placeholder="Tag number…" />
                                    </Col>
                                    <Col xs={12} md={3}>
                                        <Label className="form-label small mb-1">Color Type</Label>
                                        <Input value={filters.ColorType} onChange={e => setFilter('ColorType', e.target.value)} onKeyDown={handleFilterEnter} placeholder="e.g. BLACK/RED…" />
                                    </Col>
                                    <Col xs={12} md={3}>
                                        <Label className="form-label small mb-1">Status</Label>
                                        <Select
                                            isClearable
                                            options={statusOptions}
                                            value={filters.Status}
                                            onChange={v => setFilter('Status', v)}
                                            placeholder="Any status…"
                                            styles={selectStyles}
                                        />
                                    </Col>
                                    <Col xs={12} md={3}>
                                        <Label className="form-label small mb-1">Arriving Flight</Label>
                                        <Input value={filters.LocationHistory} onChange={e => setFilter('LocationHistory', e.target.value)} onKeyDown={handleFilterEnter} placeholder="e.g. MQ123…" />
                                    </Col>
                                    <Col xs={12} md={3}>
                                        <Label className="form-label small mb-1">Rush Tag Number</Label>
                                        <Input value={filters.RushTagNumber} onChange={e => setFilter('RushTagNumber', e.target.value)} onKeyDown={handleFilterEnter} placeholder="Rush tag…" />
                                    </Col>
                                </Row>

                                {/* Dates */}
                                <p className="text-muted fw-semibold text-uppercase mb-2 mt-3" style={{ fontSize: '0.7rem', letterSpacing: '0.08em' }}>
                                    Dates
                                </p>
                                <Row className="g-2 mb-2">
                                    <Col xs={12} md={3}>
                                        <Label className="form-label small mb-1">Flight Date</Label>
                                        <Flatpickr
                                            value={filters.CaseFlightDate}
                                            options={mergeOperationalDateCaps({ mode: 'range', altInput: true, altFormat: 'M j, Y', dateFormat: 'Y-m-d' })}
                                            onChange={(dates) => setFilter('CaseFlightDate', dates.map(d => moment(d).format('YYYY-MM-DD')))}
                                            className="form-control"
                                            placeholder="Date or range…"
                                        />
                                    </Col>
                                    {showReceiveDate && (
                                        <Col xs={12} md={3}>
                                            <Label className="form-label small mb-1">Received Date</Label>
                                            <Flatpickr
                                                value={filters.ReceiveDate}
                                                options={mergeOperationalDateCaps({ mode: 'range', altInput: true, altFormat: 'M j, Y', dateFormat: 'Y-m-d' })}
                                                onChange={(dates) => setFilter('ReceiveDate', dates.map(d => moment(d).format('YYYY-MM-DD')))}
                                                className="form-control"
                                                placeholder="Date or range…"
                                            />
                                        </Col>
                                    )}
                                    {showDeliverDate && (
                                        <Col xs={12} md={3}>
                                            <Label className="form-label small mb-1">Deliver Date</Label>
                                            <Flatpickr
                                                value={filters.DeliverDate}
                                                options={mergeOperationalDateCaps({ mode: 'range', altInput: true, altFormat: 'M j, Y', dateFormat: 'Y-m-d' })}
                                                onChange={(dates) => setFilter('DeliverDate', dates.map(d => moment(d).format('YYYY-MM-DD')))}
                                                className="form-control"
                                                placeholder="Date or range…"
                                            />
                                        </Col>
                                    )}
                                    {showForwardDate && (
                                        <Col xs={12} md={3}>
                                            <Label className="form-label small mb-1">Forward Date</Label>
                                            <Flatpickr
                                                value={filters.ForwardDate}
                                                options={mergeOperationalDateCaps({ mode: 'range', altInput: true, altFormat: 'M j, Y', dateFormat: 'Y-m-d' })}
                                                onChange={(dates) => setFilter('ForwardDate', dates.map(d => moment(d).format('YYYY-MM-DD')))}
                                                className="form-control"
                                                placeholder="Date or range…"
                                            />
                                        </Col>
                                    )}
                                </Row>

                                {/* Transit */}
                                <p className="text-muted fw-semibold text-uppercase mb-2 mt-3" style={{ fontSize: '0.7rem', letterSpacing: '0.08em' }}>
                                    Transit
                                </p>
                                <Row className="g-2 mb-2">
                                    <Col xs={12} md={3}>
                                        <Label className="form-label small mb-1">Received Flight</Label>
                                        <Input value={filters.ReceivedFlight} onChange={e => setFilter('ReceivedFlight', e.target.value)} onKeyDown={handleFilterEnter} placeholder="Flight number" />
                                    </Col>
                                    <Col xs={12} md={3}>
                                        <Label className="form-label small mb-1">Forward Flight</Label>
                                        <Input value={filters.ForwardFlight} onChange={e => setFilter('ForwardFlight', e.target.value)} onKeyDown={handleFilterEnter} placeholder="Flight number" />
                                    </Col>
                                    <Col xs={12} md={3}>
                                        <Label className="form-label small mb-1">Forward Type</Label>
                                        <Select
                                            isClearable
                                            options={forwardTypeOptions}
                                            value={filters.ForwardType}
                                            onChange={v => setFilter('ForwardType', v)}
                                            placeholder="Any type…"
                                            styles={selectStyles}
                                        />
                                    </Col>
                                </Row>

                                {/* Filter actions */}
                                <Row className="mt-3 mb-2">
                                    <Col className="d-flex justify-content-end gap-2">
                                        <Button color="secondary" outline size="sm" onClick={resetFinderState} disabled={loading}>
                                            <i className="mdi mdi-restore me-1" />Reset filters
                                        </Button>
                                        <Button color="primary" size="sm" onClick={() => doSearch()} disabled={loading}>
                                            <i className="mdi mdi-magnify me-1" />Search
                                        </Button>
                                    </Col>
                                </Row>

                            </CardBody>
                        </Card>
                    </Collapse>

                    {/* ── Loading state ──────────────────────────────────── */}
                    {loading && (
                        <div className="text-center py-5">
                            <Spinner color="primary" style={{ width: '2.5rem', height: '2.5rem' }} />
                            <p className="mt-3 mb-0" style={{ color: themeType === 'dark' ? '#adb5bd' : '#6c757d' }}>Searching…</p>
                        </div>
                    )}

                    {/* ── Empty / prompt states ──────────────────────────── */}
                    {!loading && !searched && (
                        <div className="text-center py-5" style={{ color: themeType === 'dark' ? '#adb5bd' : '#6c757d' }}>
                            <i className="mdi mdi-magnify text-primary" style={{ fontSize: '3rem' }} />
                            <p className="mt-3 mb-1">Use the search bar above to find bags.</p>
                            <small>
                                Hardware barcode scanners work too — just scan and the results appear instantly.
                                <br />
                                Press <kbd>/</kbd> to quickly focus the search bar.
                            </small>
                        </div>
                    )}

                    {!loading && searched && results.length === 0 && (
                        <div className="text-center py-5" style={{ color: themeType === 'dark' ? '#adb5bd' : '#6c757d' }}>
                            <i className="mdi mdi-bag-suitcase-off" style={{ fontSize: '3rem', color: themeType === 'dark' ? '#adb5bd' : '#6c757d' }} />
                            <p className="mt-3 mb-1">No bags found.</p>
                            <small>Try a different tag number, reference, or passenger name.</small>
                        </div>
                    )}

                    {/* ── Results ────────────────────────────────────────── */}
                    {!loading && results.length > 0 && (
                        <>
                            <div className="d-flex align-items-center gap-2 mb-3">
                                <span className="small" style={{ color: themeType === 'dark' ? '#adb5bd' : '#6c757d' }}>
                                    {ovf
                                        ? `Showing top ${results.length} result${results.length !== 1 ? 's' : ''} — refine your search for more`
                                        : `${results.length} bag${results.length !== 1 ? 's' : ''} found`
                                    }
                                </span>
                                {ovf && <Badge color="warning" pill>Results truncted</Badge>}
                            </div>

                            <Row>
                                {results.map(bag => {
                                    const statusName   = bag.bag_status?.name;
                                    const borderColor  = STATUS_BORDER[statusName] || '#6c757d';
                                    const isClosed     = !!bag.case_file?.closed;

                                    return (
                                        <Col key={bag.id} xs={12} md={6} xl={4} className="mb-3">
                                            <Card
                                                className="h-100 shadow-sm border-0"
                                                style={{ borderLeft: `4px solid ${borderColor}`, borderRadius: '6px' }}
                                            >
                                                <CardBody className="pb-2">

                                                    {/* Tag + badges */}
                                                    <div className="mb-2">
                                                        <h5 className="mb-1 fw-bold" style={{ fontFamily: 'monospace', letterSpacing: '0.05em', color: themeType === 'dark' ? '#fff' : undefined }}>
                                                            {bag.tag_number}
                                                        </h5>
                                                        <div className="d-flex gap-1 flex-wrap align-items-center">
                                                            {statusBadge(bag)}
                                                            {bag.case_file?.type && typeBadge(bag.case_file.type)}
                                                            {bag.case_file?.reference && (
                                                                <a
                                                                    href="#"
                                                                    className="small fw-semibold text-decoration-underline"
                                                                    style={{ color: themeType === 'dark' ? '#6ea8fe' : '#0d6efd', verticalAlign: 'baseline' }}
                                                                    title="Click to search Bag Finder by this reference. Select text to copy."
                                                                    onClick={(e) => {
                                                                        e.preventDefault();
                                                                        e.stopPropagation();
                                                                        searchByCaseReference(bag.case_file.reference);
                                                                    }}
                                                                >
                                                                    {bag.case_file.reference}
                                                                </a>
                                                            )}
                                                            {isClosed && <Badge color="secondary" pill>Closed</Badge>}
                                                        </div>
                                                    </div>

                                                    {/* Details */}
                                                    <div className="small" style={{ lineHeight: '1.7', color: themeType === 'dark' ? '#e9ecef' : '#495057' }}>
                                                        {bag.case_file?.full_name && (
                                                            <div>
                                                                <i className="mdi mdi-account me-1" style={{ color: themeType === 'dark' ? '#adb5bd' : '#adb5bd' }} />
                                                                {bag.case_file.full_name}
                                                                {bag.case_file?.address?.name && <span style={{ color: themeType === 'dark' ? '#adb5bd' : '#6c757d' }} className="ms-1">· {bag.case_file.address.name}</span>}
                                                            </div>
                                                        )}
                                                        {(bag.case_file?.flight || bag.case_file?.flight_date) && (
                                                            <div>
                                                                <i className="mdi mdi-airplane-landing me-1" style={{ color: themeType === 'dark' ? '#adb5bd' : '#adb5bd' }} />
                                                                {bag.case_file.flight && <span className="fw-semibold">{bag.case_file.flight}</span>}
                                                                {bag.case_file.flight_date && <span style={{ color: themeType === 'dark' ? '#adb5bd' : '#6c757d' }} className="ms-1">· {moment(bag.case_file.flight_date).format('MMM D, YYYY')}</span>}
                                                            </div>
                                                        )}
                                                        {bag.color_type && (
                                                            <div>
                                                                <i className="mdi mdi-palette me-1" style={{ color: themeType === 'dark' ? '#adb5bd' : '#adb5bd' }} />
                                                                {bag.color_type}
                                                            </div>
                                                        )}
                                                        {bag.location_history && (
                                                            <div>
                                                                <i className="mdi mdi-map-marker me-1" style={{ color: themeType === 'dark' ? '#adb5bd' : '#adb5bd' }} />
                                                                {bag.location_history}
                                                            </div>
                                                        )}
                                                        {bag.receive_date && (
                                                            <div>
                                                                <i className="mdi mdi-calendar-check me-1" style={{ color: themeType === 'dark' ? '#adb5bd' : '#adb5bd' }} />
                                                                Received: {moment(bag.receive_date).format('MMM D, YYYY')}
                                                                {bag.received_flight && <span style={{ color: themeType === 'dark' ? '#adb5bd' : '#6c757d' }} className="ms-1">· {bag.received_flight}</span>}
                                                                {bag.received_weight && <span style={{ color: themeType === 'dark' ? '#adb5bd' : '#6c757d' }} className="ms-1">· {bag.received_weight} kg</span>}
                                                            </div>
                                                        )}
                                                        {bag.deliver_date && (
                                                            <div>
                                                                <i className="mdi mdi-hand-okay me-1" style={{ color: themeType === 'dark' ? '#adb5bd' : '#adb5bd' }} />
                                                                Delivered: {moment(bag.deliver_date).format('HH:mm · MMM D, YYYY')}
                                                                {bag.delivery_name && <span style={{ color: themeType === 'dark' ? '#adb5bd' : '#6c757d' }} className="ms-1">· {bag.delivery_name}</span>}
                                                            </div>
                                                        )}
                                                        {bag.forward_date && (
                                                            <div>
                                                                <i className="mdi mdi-airplane-takeoff me-1" style={{ color: themeType === 'dark' ? '#adb5bd' : '#adb5bd' }} />
                                                                Forwarded: {moment(bag.forward_date).format('MMM D, YYYY')}
                                                                {bag.forward_flight && <span style={{ color: themeType === 'dark' ? '#adb5bd' : '#6c757d' }} className="ms-1">· {bag.forward_flight}</span>}
                                                                {bag.forward_type?.name && <span style={{ color: themeType === 'dark' ? '#adb5bd' : '#6c757d' }} className="ms-1">· {bag.forward_type.name}</span>}
                                                            </div>
                                                        )}
                                                        {bag.rush_tag_number && (
                                                            <div>
                                                                <i className="mdi mdi-tag me-1" style={{ color: themeType === 'dark' ? '#adb5bd' : '#adb5bd' }} />
                                                                Rush: <span className="fw-semibold">{bag.rush_tag_number}</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </CardBody>

                                                {/* Actions */}
                                                <div className="px-3 pb-2 pt-2 d-flex gap-2 flex-wrap align-items-center" style={{ borderTop: `1px solid ${themeType === 'dark' ? 'rgba(255,255,255,.08)' : 'rgba(0,0,0,.08)'}` }}>
                                                    {/* Contextual workflow actions */}
                                                    {!isClosed && statusName === 'TRACING' && (
                                                        <>
                                                            {canLocate && (
                                                                <Button size="sm" color="info" onClick={() => setActionModal({ type: 'locate', bag })}>
                                                                    <i className="mdi mdi-map-marker-check me-1" />Locate
                                                                </Button>
                                                            )}
                                                            {canReceive && (
                                                                <Button
                                                                    size="sm"
                                                                    color="warning"
                                                                    outline
                                                                    title="Receive now without locating first"
                                                                    onClick={() => setActionModal({ type: 'receive', bag })}
                                                                >
                                                                    <i className="mdi mdi-package-down me-1" />Receive
                                                                </Button>
                                                            )}
                                                        </>
                                                    )}
                                                    {!isClosed && statusName === 'PROCESSING' && canReceive && (
                                                        <Button
                                                            size="sm"
                                                            color="warning"
                                                            title="Physical receipt for DPR; locate is not used for this step"
                                                            onClick={() => setActionModal({ type: 'receive', bag })}
                                                        >
                                                            <i className="mdi mdi-package-down me-1" />Receive
                                                        </Button>
                                                    )}
                                                    {!isClosed && statusName === 'LOCATED' && canReceive && (
                                                        <Button size="sm" color="warning" onClick={() => setActionModal({ type: 'receive', bag })}>
                                                            <i className="mdi mdi-package-down me-1" />Receive
                                                        </Button>
                                                    )}
                                                    {!isClosed && statusName === 'RECEIVED' && (
                                                        <>
                                                            {canDeliver && (
                                                                <Button size="sm" color="success" onClick={() => setActionModal({ type: 'deliver', bag })}>
                                                                    <i className="mdi mdi-hand-okay me-1" />Deliver
                                                                </Button>
                                                            )}
                                                            {canForward && (
                                                                <Button size="sm" color="primary" outline onClick={() => setActionModal({ type: 'forward', bag })}>
                                                                    <i className="mdi mdi-airplane-takeoff me-1" />Forward
                                                                </Button>
                                                            )}
                                                            {canDispose && (
                                                                <Button size="sm" color="danger" outline onClick={() => setActionModal({ type: 'dispose', bag })}>
                                                                    <i className="mdi mdi-delete-forever me-1" />Dispose
                                                                </Button>
                                                            )}
                                                        </>
                                                    )}

                                                    {/* Utility actions pushed to the right */}
                                                    <div className="ms-auto d-flex gap-2">
                                                        <Button
                                                            size="sm"
                                                            color="secondary"
                                                            outline
                                                            title="View details"
                                                            style={themeType === 'dark' ? { color: '#adb5bd', borderColor: '#6c757d' } : {}}
                                                            onClick={async () => { setViewBag(await withSignature(bag)); setViewOpen(true); }}
                                                        >
                                                            <i className="mdi mdi-eye" />
                                                        </Button>
                                                        {!isClosed && (
                                                            <>
                                                                <Button
                                                                    size="sm"
                                                                    color="secondary"
                                                                    outline
                                                                    title="Edit bag"
                                                                    style={themeType === 'dark' ? { color: '#adb5bd', borderColor: '#6c757d' } : {}}
                                                                    onClick={async () => toggleCUModal({ row: await withSignature(bag), mode: 'Update' })}
                                                                >
                                                                    <i className="mdi mdi-pencil" />
                                                                </Button>
                                                                <Button
                                                                    size="sm"
                                                                    color="danger"
                                                                    outline
                                                                    title="Delete bag"
                                                                    onClick={() => toggleConfirmModal({
                                                                        row: bag,
                                                                        request: deleteDataRequest,
                                                                        title: 'Delete Bag',
                                                                        body: `Delete bag ${bag.tag_number}? This cannot be undone.`,
                                                                    })}
                                                                >
                                                                    <i className="mdi mdi-trash-can" />
                                                                </Button>
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                            </Card>
                                        </Col>
                                    );
                                })}
                            </Row>
                        </>
                    )}

                    {/* ── Modals ─────────────────────────────────────────── */}
                    <BagViewModal
                        isOpen={viewOpen}
                        toggle={() => { setViewOpen(false); setViewBag(null); }}
                        bag={viewBag}
                    />

                    <DynamicModal
                        config={cuModalConfig}
                        toggleModal={toggleCUModal}
                        submitModes={formModes}
                        ds={dataStructure}
                    />

                    <ConfirmDialog
                        id={confirmID}
                        params={confirmParams}
                        config={confirmConfig}
                        visible={confirmVisible}
                        toggleModal={toggleConfirmModal}
                    />

                    <BarcodeScannerModal
                        isOpen={scannerOpen}
                        toggle={() => setScannerOpen(o => !o)}
                        onScan={applyScannedSearch}
                    />

                    {/* ── Bag action modals ───────────────────────────────── */}
                    {actionModal?.type === 'locate' && (
                        <LocateModal
                            bag={actionModal.bag}
                            themeType={themeType}
                            onClose={() => setActionModal(null)}
                            onSuccess={updated => { setResults(prev => prev.map(r => r.id === updated.id ? updated : r)); setActionModal(null); }}
                        />
                    )}
                    {actionModal?.type === 'receive' && (
                        <ReceiveModal
                            bag={actionModal.bag}
                            themeType={themeType}
                            onClose={() => setActionModal(null)}
                            onSuccess={updated => { setResults(prev => prev.map(r => r.id === updated.id ? updated : r)); setActionModal(null); }}
                        />
                    )}
                    {actionModal?.type === 'deliver' && (
                        <DeliverModal
                            bag={actionModal.bag}
                            themeType={themeType}
                            onClose={() => setActionModal(null)}
                            onSuccess={updated => { setResults(prev => prev.map(r => r.id === updated.id ? updated : r)); setActionModal(null); }}
                        />
                    )}
                    {actionModal?.type === 'forward' && (
                        <ForwardModal
                            bag={actionModal.bag}
                            themeType={themeType}
                            forwardTypeOptions={forwardTypeOptions}
                            onClose={() => setActionModal(null)}
                            onSuccess={updated => { setResults(prev => prev.map(r => r.id === updated.id ? updated : r)); setActionModal(null); }}
                        />
                    )}
                    {actionModal?.type === 'dispose' && (
                        <DisposeModal
                            bag={actionModal.bag}
                            themeType={themeType}
                            onClose={() => setActionModal(null)}
                            onSuccess={updated => { setResults(prev => prev.map(r => r.id === updated.id ? updated : r)); setActionModal(null); }}
                        />
                    )}

                </Container>
            </div>
        </React.Fragment>
    );
};

export default withRouter(BagFinder);
